import { Dropdown, Option, OptionOnSelectData, SelectionEvents } from '@fluentui/react-components'
import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import { useQueryClient } from '@tanstack/react-query'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { Button } from '../Button'
import { ErrorComponent } from '../ErrorComponent'
import { Loading } from '../Loading'
import { TeamRoleCard } from '../TeamRoleCard/TeamRoleCard'
import { MessagesSubscriptionsOrganisationTeamUser } from '@/dotnet-client'

type MessagesSubscriptionsOrganisationTeamRole = RestSchemas['Messages.Subscriptions.OrganisationTeamRole']
type MessagesApplicationsApplicationRole = RestSchemas['Messages.Applications.ApplicationRole']

export const MaintainSupportTeamRoles = ({
  orgTeamUser,
}: {
  orgTeamUser: MessagesSubscriptionsOrganisationTeamUser
}) => {
  const queryClient = useQueryClient()
  const appId = orgTeamUser.applicationId

  const {
    getApplicationsApiListApplicationRoles,
    getSubscriptionsApiListTeamRoles,
    putSubscriptionsApiAddTeamRole,
    deleteSubscriptionsApiDeleteTeamRole,
    getApplicationsApiGetApplicationTeamsForUserOrganization,
  } = createRestClient()

  const {
    data: applicationRoles,
    isPending: isApplicationRolesPending,
    isError: isApplicationRolesError,
  } = getApplicationsApiListApplicationRoles.useQuery({ query: { applicationId: appId } }, { enabled: !!appId })

  const teamRolesSelector = useCallback(
    (data: MessagesSubscriptionsOrganisationTeamRole[]) =>
      applicationRoles?.filter((role) => data.map((tRole) => tRole.roleId).includes(role.id)),
    [applicationRoles]
  )

  const {
    data: supportTeamRoles,
    isPending: isSupportTeamRolesPending,
    isError: isSupportTeamRolesError,
  } = getSubscriptionsApiListTeamRoles.useQuery(
    { query: { teamId: orgTeamUser.teamId } },
    {
      select: teamRolesSelector,
      enabled: !!applicationRoles,
    }
  )

  const invalidateTeamRoles = () =>
    getSubscriptionsApiListTeamRoles.invalidateQueries(
      { parameters: { query: { teamId: orgTeamUser.teamId } } },
      queryClient
    )

  const invalidateTeams = () =>
    getApplicationsApiGetApplicationTeamsForUserOrganization.invalidateQueries(
      { parameters: { path: { appId } } },
      queryClient
    )

  const invalidateQueries = () => {
    invalidateTeamRoles()
    invalidateTeams()
  }

  const { mutateAsync: addTeamRole } = putSubscriptionsApiAddTeamRole.useMutation()
  const { mutate: deleteTeamRole } = deleteSubscriptionsApiDeleteTeamRole.useMutation()

  const [selectedOptions, setSelectedOptions] = useState<MessagesApplicationsApplicationRole[]>([])

  const onOptionSelect = (_event: SelectionEvents, data: OptionOnSelectData) => {
    if (!applicationRoles) return
    const _selectedOptions = applicationRoles.filter((role) => data.selectedOptions.includes(role.name))
    setSelectedOptions(_selectedOptions)
  }

  const onAddSubmit = () => {
    Promise.all(
      selectedOptions.map((role) =>
        addTeamRole(
          { body: { roleId: role.id, teamId: orgTeamUser.teamId } },
          {
            onError: () => toast.error('Error adding team role'),
            onSuccess: () => toast.success('Team role added'),
          }
        )
      )
    ).finally(() => {
      setSelectedOptions([])
      invalidateQueries()
    })
  }

  const onDelete = (role: MessagesApplicationsApplicationRole) => {
    deleteTeamRole(
      { body: { roleId: role.id, teamId: orgTeamUser.teamId } },
      {
        onError: () => toast.error('Error deleting team role'),
        onSuccess: () => {
          toast.success('Team role deleted')
          invalidateQueries()
        },
      }
    )
  }

  if (isApplicationRolesPending || isSupportTeamRolesPending) return <Loading />
  if (isApplicationRolesError || isSupportTeamRolesError || !supportTeamRoles) return <ErrorComponent />

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', paddingBottom: '20px' }}>
      <Dropdown
        aria-labelledby={'add-org-user-to-team'}
        multiselect={true}
        placeholder="Add team roles"
        value={selectedOptions.map((option) => option.name).join(', ')}
        selectedOptions={selectedOptions.map((option) => option.name)}
        onOptionSelect={onOptionSelect}
      >
        <div style={{ maxHeight: '600px', msOverflowY: 'scroll' }}>
          {applicationRoles.map((option) => (
            <Option
              key={`${option.id}-${option.applicationId}`}
              disabled={supportTeamRoles.map((role) => role.id).includes(option.id)}
            >
              {option.name}
            </Option>
          ))}
        </div>
      </Dropdown>
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'end' }}>
        <Button text="Add roles" onClick={onAddSubmit} />
      </div>
      {supportTeamRoles.map((role) => (
        <TeamRoleCard key={`${role.id}-${role.applicationId}`} role={role} onDelete={() => onDelete(role)} />
      ))}
    </div>
  )
}
